import type { CarGroup, MemberGroup } from './notificationGroupTypes';
import { acceptsAnyCarSms } from './notificationReach';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';

export type DriverChangeParty = {
  userGroupMemberId: number;
  name: string;
  smsBookingNotifications: boolean;
  smsRecallProposals: boolean;
  managedCarCount: number;
  /** Admins see the whole fleet, so driving a car changes only their SMS, never what they can see. */
  isAdmin: boolean;
};

/**
 * One car changing hands. The endpoint keeps exactly one driver per car, so every
 * assignment also takes the car off whoever holds it — the pair, not the pick alone,
 * is what the admin has to agree to.
 */
export type DriverChange = {
  userGroupCarId: number;
  carId: number;
  licensePlateNumber: string;
  carName: string;
  incoming: DriverChangeParty;
  /** Absent when the car has no driver yet: the change adds one instead of swapping. */
  outgoing?: DriverChangeParty;
};

export type DriverChangeEffect = {
  /** Warnings are the ones worth stopping for; the rest confirm what the admin already expects. */
  tone: 'neutral' | 'warning';
  text: string;
};

export type CarManagerPatch = {
  userGroupCarId: number;
  userGroupMemberId: number;
};

const toParty = (member: MemberGroup): DriverChangeParty => ({
  userGroupMemberId: member.userGroupMemberId,
  name: `${member.firstName} ${member.lastName}`.trim(),
  smsBookingNotifications: member.smsBookingNotifications,
  smsRecallProposals: member.smsRecallProposals,
  managedCarCount: member.managedCarCount,
  isAdmin: member.isAdmin
});

/**
 * Build one assignment from the current car record and selected member.
 */
export const buildDriverChange = ({ car, incoming }: { car: CarGroup; incoming: MemberGroup }): DriverChange => {
  return {
    userGroupCarId: car.userGroupCarId,
    carId: car.carId,
    licensePlateNumber: car.licensePlate.number,
    carName: `${car.make} ${car.model}`.trim(),
    incoming: toParty(incoming),
    outgoing:
      car.manager && car.manager.userGroupMemberId !== incoming.userGroupMemberId ? toParty(car.manager) : undefined
  };
};

export const toCarManagerPatches = (changes: DriverChange[]): CarManagerPatch[] =>
  changes.map((change) => ({
    userGroupCarId: change.userGroupCarId,
    userGroupMemberId: change.incoming.userGroupMemberId
  }));

/** One car names its plate; a batch names how many. */
export const driverChangeMessage = (changes: DriverChange[], t: NotificationGroupOverviewTranslations) =>
  changes.length === 1 ? t.driverSaved(changes[0]!.licensePlateNumber) : t.driversSaved(changes.length);

const smsTypeLabel = (party: DriverChangeParty, t: NotificationGroupOverviewTranslations) => {
  if (party.smsBookingNotifications && party.smsRecallProposals) {
    return t.bothSmsTypes;
  }
  return party.smsBookingNotifications ? t.bookingSmsInline : t.serviceReminderSmsInline;
};

/** A plate on its own reads better than "1 car"; past that the count carries it. */
const carsLabel = (changes: DriverChange[], t: NotificationGroupOverviewTranslations) =>
  changes.length === 1 ? changes[0]!.licensePlateNumber : t.carCount(changes.length);

const groupByOutgoing = (changes: DriverChange[]) => {
  const groups = new Map<number, { party: DriverChangeParty; changes: DriverChange[] }>();
  changes.forEach((change) => {
    if (!change.outgoing) {
      return;
    }
    const group = groups.get(change.outgoing.userGroupMemberId);
    if (group) {
      group.changes.push(change);
    } else {
      groups.set(change.outgoing.userGroupMemberId, { party: change.outgoing, changes: [change] });
    }
  });
  return Array.from(groups.values());
};

/**
 * The sentences under "What happens when you confirm". Every incoming car belongs to
 * one person, so their line is written once for the whole batch; outgoing drivers are
 * grouped, because losing two cars at once is one fact about that person, not two.
 */
export const buildDriverChangeEffects = ({
  changes,
  translations: t
}: {
  changes: DriverChange[];
  translations: NotificationGroupOverviewTranslations;
}): DriverChangeEffect[] => {
  const [first] = changes;
  if (!first) {
    return [];
  }
  const incoming = first.incoming;
  const effects: DriverChangeEffect[] = [];

  effects.push(
    acceptsAnyCarSms(incoming)
      ? {
          tone: 'neutral',
          text: t.effectIncomingReceives(incoming.name, smsTypeLabel(incoming, t), carsLabel(changes, t))
        }
      : { tone: 'warning', text: t.effectIncomingSilenced(incoming.name, carsLabel(changes, t)) }
  );
  if (!incoming.isAdmin) {
    effects.push({
      tone: 'neutral',
      text: t.effectIncomingGainsAccess(incoming.name, carsLabel(changes, t), changes.length)
    });
  }

  groupByOutgoing(changes).forEach(({ party, changes: lost }) => {
    const lostPlates = lost.map((change) => change.licensePlateNumber).join(', ');
    effects.push({ tone: 'neutral', text: t.effectOutgoingStops(party.name, lostPlates) });
    if (!party.isAdmin) {
      effects.push({ tone: 'neutral', text: t.effectOutgoingLosesAccess(party.name, lostPlates) });
    }
    const remaining = party.managedCarCount - lost.length;
    effects.push({
      tone: 'neutral',
      text: remaining > 0 ? t.effectOutgoingRemaining(party.name, remaining) : t.effectOutgoingNoneLeft(party.name)
    });
  });

  const withoutPreviousDriver = changes.filter((change) => !change.outgoing);
  if (withoutPreviousDriver.length > 0) {
    effects.push({ tone: 'neutral', text: t.effectNoPreviousDriver(carsLabel(withoutPreviousDriver, t)) });
  }
  if (changes.some((change) => change.outgoing)) {
    effects.push({ tone: 'neutral', text: t.effectMembershipUnchanged });
  }

  return effects;
};
